import React, { useEffect, useState } from 'react';
import { ArrowRight, Check, CalendarDays, Info, X } from 'lucide-react';
interface Props {
  onContinue: () => void;
}
const THEMES = [{
  id: 'climate',
  title: 'Clima e Ambiente',
  inProgram: true,
  description: 'Finanza climatica, economia circolare, sicurezza idrica e transizione energetica.'
}, {
  id: 'tech',
  title: 'Tech e Innovazione',
  inProgram: true,
  description: "Governance dell'AI, smart cities, inclusione digitale e identità digitale."
}, {
  id: 'society',
  title: 'Società ed Economia',
  inProgram: true,
  description: 'Sistemi alimentari, educazione, impact investing e sviluppo sostenibile.'
}, {
  id: 'health',
  title: 'Salute Globale',
  inProgram: false,
  description: 'Accesso alle cure, innovazione biotech, salute mentale e benessere.'
}];
export function OnboardingThemes({
  onContinue
}: Props) {
  const [selected, setSelected] = useState<string[]>([]);
  const [infoTheme, setInfoTheme] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);
  const toggleTheme = (id: string) => {
    setSelected(prev => {
      if (prev.includes(id)) {
        return prev.filter(t => t !== id);
      }
      if (prev.length >= 2) {
        return prev; // Max 2 tracks to force focus
      }
      return [...prev, id];
    });
  };
  const isValid = selected.length > 0;
  const activeInfoTheme = THEMES.find(t => t.id === infoTheme);
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] relative" data-id="element-463">
      <div className="flex-1 flex flex-col px-6 pt-12 pb-32 overflow-y-auto" data-id="element-464">
        {/* Step Indicator */}
        <div className="flex justify-center gap-2 flex-shrink-0 mb-10" style={{
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(16px)',
        transition: 'opacity 800ms cubic-bezier(0.4, 0, 0.2, 1), transform 800ms cubic-bezier(0.4, 0, 0.2, 1)',
        transitionDelay: '0ms'
      }} data-id="element-465">
          <div className="w-1.5 h-1.5 rounded-full bg-[#2A2A2A]" data-id="element-466" />
          <div className="w-6 h-1.5 rounded-full bg-[#C5D82D]" data-id="element-467" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#2A2A2A]" data-id="element-468" />
        </div>

        {/* Header */}
        <div className="mb-8 text-center" style={{
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 800ms cubic-bezier(0.4, 0, 0.2, 1), transform 800ms cubic-bezier(0.4, 0, 0.2, 1)',
        transitionDelay: '120ms'
      }} data-id="element-469">
          <h1 className="text-[24px] font-medium text-white mb-3 tracking-tight" data-id="element-470">
            Cosa ti interessa?
          </h1>
        </div>

        {/* Theme List */}
        <div className="flex flex-col gap-3" data-id="element-471">
          {THEMES.map((theme, index) => {
          const isSelected = selected.includes(theme.id);
          return <div key={theme.id} onClick={() => toggleTheme(theme.id)} className={`
                  flex items-center gap-3 rounded-xl px-4 py-4 cursor-pointer active:scale-[0.99]
                  ${isSelected ? 'bg-[#C5D82D]/5 border border-[#C5D82D]/50' : 'bg-[#141414] border border-[#2A2A2A] hover:border-[#555555]'}
                `} style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 800ms cubic-bezier(0.4, 0, 0.2, 1), transform 800ms cubic-bezier(0.4, 0, 0.2, 1), background-color 200ms ease, border-color 200ms ease',
            transitionDelay: mounted ? `${250 + index * 70}ms` : '0ms'
          }} data-id="element-472">
                {/* Checkbox Indicator */}
                <div className={`
                    w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors
                    ${isSelected ? 'bg-[#C5D82D] border-none' : 'border border-[#555555] bg-transparent'}
                  `} data-id="element-473">
                  {isSelected && <Check className="w-4 h-4 text-[#0D0D0D]" data-id="element-474" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 flex flex-col gap-1" data-id="element-475">
                  <h3 className="text-[15px] font-medium text-white truncate" data-id="element-476">
                    {theme.title}
                  </h3>
                  <div className="flex items-center gap-1.5" data-id="element-477">
                    {theme.inProgram ? <>
                        <CalendarDays className="w-3.5 h-3.5 text-[#888888]" data-id="element-478" />
                        <span className="text-[11px] text-[#888888]" data-id="element-479">
                          Nel programma
                        </span>
                      </> : <span className="text-[11px] text-[#999999]" data-id="element-480">
                        Non in programma
                      </span>}
                  </div>
                </div>

                {/* Info Button */}
                <button onClick={e => {
              e.stopPropagation();
              setInfoTheme(theme.id);
            }} className="w-8 h-8 rounded-full bg-[#1A1A1A] flex items-center justify-center text-[#888888] hover:text-white hover:bg-[#2A2A2A] transition-colors flex-shrink-0" data-id="element-481">
                  <Info className="w-4 h-4" data-id="element-482" />
                </button>
              </div>;
        })}
        </div>
      </div>

      {/* Fixed Bottom Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#0D0D0D]/90 backdrop-blur-xl border-t border-[#2A2A2A] p-6 pb-8" style={{
      opacity: mounted ? 1 : 0,
      transform: mounted ? 'translateY(0)' : 'translateY(16px)',
      transition: 'opacity 800ms cubic-bezier(0.4, 0, 0.2, 1), transform 800ms cubic-bezier(0.4, 0, 0.2, 1)',
      transitionDelay: '550ms'
    }} data-id="element-483">
        <button onClick={onContinue} disabled={!isValid} className={`
            w-full rounded-full py-3.5 font-medium text-base transition-all duration-200 flex items-center justify-center gap-2
            ${isValid ? 'bg-transparent border border-[#C5D82D] text-white active:scale-[0.98]' : 'bg-transparent border border-[#2A2A2A] text-[#888888] cursor-not-allowed'}
          `} data-id="element-484">
          Continua
          <ArrowRight className="w-4 h-4" data-id="element-485" />
        </button>
      </div>

      {/* Info Modal */}
      {infoTheme && activeInfoTheme && <div className="absolute inset-0 z-50 flex items-center justify-center p-6" data-id="element-486">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-[#0D0D0D]/80 backdrop-blur-sm" onClick={() => setInfoTheme(null)} data-id="element-487" />

          {/* Modal Content */}
          <div className="relative w-full max-w-[320px] bg-[#141414] border border-[#2A2A2A] rounded-2xl p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200" data-id="element-488">
            <button onClick={() => setInfoTheme(null)} className="absolute top-4 right-4 text-[#888888] hover:text-white transition-colors" data-id="element-489">
              <X className="w-5 h-5" data-id="element-490" />
            </button>

            <div className="mb-4" data-id="element-491">
              <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center mb-4" data-id="element-492">
                <Info className="w-5 h-5 text-[#C5D82D]" data-id="element-493" />
              </div>
              <h2 className="text-[20px] font-medium text-white mb-2" data-id="element-494">
                {activeInfoTheme.title}
              </h2>
              <div className="flex items-center gap-1.5 mb-4" data-id="element-495">
                {activeInfoTheme.inProgram ? <>
                    <CalendarDays className="w-3.5 h-3.5 text-[#C5D82D]" data-id="element-496" />
                    <span className="text-[12px] text-[#C5D82D] font-medium" data-id="element-497">
                      In programma al Summit
                    </span>
                  </> : <span className="text-[12px] text-[#999999]" data-id="element-498">
                    Non in programma
                  </span>}
              </div>
              <p className="text-[14px] text-[#888888] leading-relaxed" data-id="element-499">
                {activeInfoTheme.description}
              </p>
            </div>

            <button onClick={() => {
          if (!selected.includes(activeInfoTheme.id)) {
            toggleTheme(activeInfoTheme.id);
          }
          setInfoTheme(null);
        }} className={`
                w-full rounded-full py-3 font-medium text-[14px] transition-all duration-200
                ${selected.includes(activeInfoTheme.id) ? 'bg-[#2A2A2A] text-white' : 'bg-[#C5D82D] text-[#0D0D0D]'}
              `} data-id="element-500">
              {selected.includes(activeInfoTheme.id) ? 'Rimuovi selezione' : 'Seleziona questo tema'}
            </button>
          </div>
        </div>}
    </div>;
}